import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, DataTable, DisplayInactiveToggle, EmptyValue, FilterField, FilterGrid, LoadErrorState, ServiceAreaPill } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import ReportParametersCard from '../components/ReportParametersCard';
import type { ServiceArea } from '../../../services/areasService';
import {
  vendorService,
  type ComplianceDocType,
  type ComplianceRowStatus,
  type VendorComplianceCounts,
  type VendorComplianceFilters,
  type VendorComplianceRow,
} from '../../../services/vendorService';
import { formatDate } from '@/utils/displayDateTime';
import { COMPLIANCE_DOC_TYPE_LABELS, COMPLIANCE_STATUS_LABELS } from '../vendorCompliance';

// V2-078 – Vendor Compliance report. One row per compliance document: vendor, resource (vehicle
// insurance is per resource), document type, expiry date, status and service area. Shared filter
// panel, sortable columns, CSV export. Inactive vendors and resources are hidden unless Display
// Inactive is on (V2-075). The Home tiles open this report filtered to their status; they and
// the report come from the same server query, so the numbers match.

const STATUSES: ComplianceRowStatus[] = ['OVERDUE', 'DUE_SOON', 'MISSING', 'CURRENT'];
const DOC_TYPES: ComplianceDocType[] = ['WCB', 'LIABILITY_INSURANCE', 'VEHICLE_INSURANCE'];
const STATUS_ORDER: Record<ComplianceRowStatus, number> = { OVERDUE: 0, DUE_SOON: 1, MISSING: 2, CURRENT: 3 };

const STATUS_PILL: Record<ComplianceRowStatus, string> = {
  OVERDUE: 'tw-bg-danger/10 tw-text-danger',
  DUE_SOON: 'tw-bg-warning tw-text-warning-text',
  MISSING: 'tw-bg-danger/10 tw-text-danger',
  CURRENT: 'tw-bg-success/10 tw-text-success',
};

type SortKey = 'vendor' | 'resource' | 'docType' | 'expiresAt' | 'status' | 'serviceArea';

interface Props {
  serviceAreas: ServiceArea[];
  /** From a Home tile: open filtered to one status. */
  initialStatus?: ComplianceRowStatus | null;
}

const VendorComplianceReportView: React.FC<Props> = ({ serviceAreas, initialStatus = null }) => {
  const [statuses, setStatuses] = useState<ComplianceRowStatus[]>(initialStatus ? [initialStatus] : []);
  const [docTypes, setDocTypes] = useState<ComplianceDocType[]>([]);
  const [serviceAreaIds, setServiceAreaIds] = useState<number[]>([]);
  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [rows, setRows] = useState<VendorComplianceRow[]>([]);
  const [counts, setCounts] = useState<VendorComplianceCounts | null>(null);
  const [vendorOptions, setVendorOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filters: VendorComplianceFilters = useMemo(
    () => ({ status: statuses, docType: docTypes, serviceAreaIds, vendorIds, includeInactive }),
    [statuses, docTypes, serviceAreaIds, vendorIds, includeInactive],
  );

  const run = useCallback(async (next: VendorComplianceFilters) => {
    setLoading(true);
    setFailed(false);
    try {
      const result = await vendorService.getComplianceReport(next);
      setRows(result.rows);
      setCounts(result.counts);
      // The vendor filter offers every vendor seen so far, so a selection never disappears.
      setVendorOptions((prev) => {
        const byId = new Map(prev.map((v) => [v.id, v]));
        result.rows.forEach((r) => byId.set(r.vendorId, { id: r.vendorId, name: r.vendorName }));
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
      });
    } catch (err) {
      console.error('Vendor compliance report failed to load', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Runs once on open (with a tile's status), then on Run Report and on Display Inactive.
  useEffect(() => {
    void run(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, includeInactive]);

  const sorted = useMemo(() => {
    if (!sortBy) return rows;
    const value = (r: VendorComplianceRow): string | number => {
      switch (sortBy) {
        case 'vendor':
          return r.vendorName.toLowerCase();
        case 'resource':
          return (r.resourceName ?? '').toLowerCase();
        case 'docType':
          return COMPLIANCE_DOC_TYPE_LABELS[r.docType];
        case 'expiresAt':
          return r.expiresAt ? new Date(r.expiresAt).getTime() : Number.POSITIVE_INFINITY;
        case 'status':
          return STATUS_ORDER[r.status];
        case 'serviceArea':
          return r.serviceAreas.map((sa) => sa.name).join(', ');
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }, [rows, sortBy, sortDir]);

  const columns: DataTableColumn<VendorComplianceRow>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      sortable: true,
      mobile: 'title',
      cell: (r) => <Link to={`/vendors/${r.vendorId}`}>{r.vendorName}</Link>,
    },
    {
      key: 'resource',
      header: 'Resource',
      sortable: true,
      cell: (r) => r.resourceName ?? <EmptyValue />,
    },
    {
      key: 'docType',
      header: 'Document Type',
      sortable: true,
      mobile: 'subtitle',
      cell: (r) => COMPLIANCE_DOC_TYPE_LABELS[r.docType],
    },
    {
      key: 'expiresAt',
      header: 'Expiry Date',
      sortable: true,
      cell: (r) => (r.expiresAt ? formatDate(r.expiresAt.slice(0, 10)) : <EmptyValue />),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      cell: (r) => (
        <span className={`tw-inline-flex tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${STATUS_PILL[r.status]}`}>
          {COMPLIANCE_STATUS_LABELS[r.status]}
        </span>
      ),
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      // V3-033 / FMT: the neutral slate Service Area pill, one line each.
      cell: (r) =>
        r.serviceAreas.length ? (
          <span className="tw-flex tw-flex-wrap tw-gap-1">
            {r.serviceAreas.map((sa) => (
              <ServiceAreaPill key={sa.id ?? sa.name} value={sa} />
            ))}
          </span>
        ) : (
          <EmptyValue />
        ),
    },
  ];

  const exportCsv = () => {
    window.location.assign(vendorService.complianceReportCsvUrl(filters));
  };

  return (
    <div className="tw-flex tw-flex-col">
      <ReportParametersCard
        title="Vendor Compliance"
        onGenerate={() => void run(filters)}
        onExport={exportCsv}
        loadingGenerate={loading}
        disableExport={loading || rows.length === 0}
        generateButtonText="Run Report"
        rightColumn={
          <DisplayInactiveToggle id="vendor-compliance-inactive" checked={includeInactive} onChange={setIncludeInactive} />
        }
      >
        <FilterGrid>
          <FilterField>
            <MultiSelectDropdown
              label="Status"
              options={STATUSES}
              value={statuses}
              onChange={(v) => setStatuses(v as ComplianceRowStatus[])}
              getValue={(s) => s}
              formatOption={(s) => COMPLIANCE_STATUS_LABELS[s]}
              placeholder="All Statuses"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              label="Document Type"
              options={DOC_TYPES}
              value={docTypes}
              onChange={(v) => setDocTypes(v as ComplianceDocType[])}
              getValue={(d) => d}
              formatOption={(d) => COMPLIANCE_DOC_TYPE_LABELS[d]}
              placeholder="All Document Types"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              label="Service Area"
              options={serviceAreas}
              value={serviceAreaIds}
              onChange={(v) => setServiceAreaIds(v.map(Number))}
              getValue={(sa) => sa.id}
              formatOption={(sa) => sa.name}
              placeholder="All Service Areas"
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              label="Vendor"
              options={vendorOptions}
              value={vendorIds}
              onChange={(v) => setVendorIds(v.map(Number))}
              getValue={(v) => v.id}
              formatOption={(v) => v.name}
              placeholder="All Vendors"
            />
          </FilterField>
        </FilterGrid>
      </ReportParametersCard>

      {failed && (
        <LoadErrorState className="tw-mt-3" what="the vendor compliance report" onRetry={() => void run(filters)} />
      )}

      <Card className="tw-mt-3">
        <Card.Header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <h6 className="tw-mb-0">Vendor Compliance ({rows.length} documents)</h6>
          {counts && (
            <span className="tw-text-sm tw-text-content-muted" data-testid="vendor-compliance-counts">
              {counts.overdue} overdue · {counts.dueSoon} due in 30 days · {counts.missing} missing
            </span>
          )}
        </Card.Header>
        <Card.Body className="tw-p-0">
          <DataTable
            columns={columns}
            data={sorted}
            rowKey={(r) => `${r.vendorId}-${r.docType}-${r.resourceId ?? 0}`}
            loading={loading}
            emptyMessage="No compliance documents match these filters."
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={(key, dir) => {
              setSortBy(key as SortKey);
              setSortDir(dir);
            }}
            stickyHeader
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default VendorComplianceReportView;
